// Ad Animator — shared constants + prompt builder.
//
// The spec is explicit: the short base prompt performs better than long,
// detailed prompts, so DO NOT add extra creative instructions here. The
// builder only appends a single music sentence (omitted for "No Music",
// uses the custom string when "Custom") and one short intensity modifier.

export const AD_ANIMATOR_DURATIONS = [5, 10, 15] as const;
export type AdAnimatorDuration = (typeof AD_ANIMATOR_DURATIONS)[number];

export const AD_ANIMATOR_ASPECT_RATIOS = [
  "Original",
  "9:16",
  "4:5",
  "1:1",
  "16:9",
] as const;
export type AdAnimatorAspectRatio = (typeof AD_ANIMATOR_ASPECT_RATIOS)[number];

export const AD_ANIMATOR_INTENSITIES = [
  "Conservative",
  "Standard",
  "High",
] as const;
export type AdAnimatorIntensity = (typeof AD_ANIMATOR_INTENSITIES)[number];

export const AD_ANIMATOR_MUSIC_STYLES = [
  "No Music",
  "American Country",
  "Upbeat Pop",
  "Hip Hop",
  "Electronic",
  "Cinematic",
  "Lo-fi",
  "Acoustic",
  "Emotional",
  "Luxury",
  "Funny / Playful",
  "Custom",
] as const;
export type AdAnimatorMusicStyle = (typeof AD_ANIMATOR_MUSIC_STYLES)[number];

// Single source of truth for whether a job's music style should request
// provider-side audio. Any style other than "No Music" (including "Custom")
// must produce an audible clip, so the Seedance `generate_audio` flag is set.
// The worker derives the flag from this so a future refactor can't silently
// revert music-style ads back to silent clips without breaking its test.
export function adAnimatorShouldGenerateAudio(musicStyle: string): boolean {
  return musicStyle !== "No Music";
}

export const AD_ANIMATOR_VARIATION_OPTIONS = [1, 2, 3, 4] as const;

// Ordered progress states surfaced to the UI as the job advances.
export const AD_ANIMATOR_PROGRESS_STATES = [
  "Preparing prompt",
  "Uploading image",
  "Submitting to Seedance",
  "Generating animation",
  "Processing video",
  "Saving output",
  "Complete",
] as const;

export interface BuildAdAnimatorPromptArgs {
  musicStyle: string;
  customMusicStyle?: string | null;
  intensity: AdAnimatorIntensity | string;
  // Optional, layered prompt editing inputs:
  //  - customInstructions: a short note appended to the proven base prompt.
  //  - promptOverride: a full freeform prompt used VERBATIM (ignores the base
  //    prompt and all settings-derived sentences below).
  // When neither is provided the output is byte-for-byte the legacy auto-prompt.
  customInstructions?: string | null;
  promptOverride?: string | null;
}

export function buildAdAnimatorPrompt({
  musicStyle,
  customMusicStyle,
  intensity,
  customInstructions,
  promptOverride,
}: BuildAdAnimatorPromptArgs): string {
  // A full override wins outright: use exactly what the user wrote.
  const override = typeof promptOverride === "string" ? promptOverride.trim() : "";
  if (override) return override;

  let prompt =
    "Reference @Image1 and animate the background of the ad with something relevant to the product being featured. Leave the main components of the ad in place, make sure everything is legible but VERY animate the ad so it is a scroll stopping ad for social media.";

  if (musicStyle !== "No Music") {
    const selectedMusic =
      musicStyle === "Custom" && customMusicStyle
        ? customMusicStyle
        : musicStyle;
    prompt += ` Use ${selectedMusic} music in the background.`;
  }

  if (intensity === "Conservative") {
    prompt += " Keep the motion clean, subtle, and premium.";
  } else if (intensity === "Standard") {
    prompt += " Add noticeable motion while keeping the ad clean and readable.";
  } else if (intensity === "High") {
    prompt +=
      " Push the motion harder and make the ad feel very lively, but do not redesign or distort the ad.";
  }

  // Append the user's short custom instructions at the very end of the base
  // prompt (default "Add instructions" mode).
  const note = typeof customInstructions === "string" ? customInstructions.trim() : "";
  if (note) prompt += ` ${note}`;

  return prompt;
}

// ── 9:16 → 4:5 safe zone (Ad Animator) ──────────────────────────────────────
// When the job's OUTPUT ratio is 9:16 (including animating a 4:5 source ad
// into 9:16), the expanded top/bottom areas must stay background-only motion:
// the original ad content is kept centered and unscaled in the middle 4:5
// region. Applied at RENDER time by the worker, so it is NOT bypassed by a
// user prompt override (`promptOverride` returns verbatim from the builder,
// but the worker still appends this sentence for 9:16 jobs). Any other ratio
// returns the prompt unchanged, byte-for-byte.
export const AD_ANIMATOR_SAFE_ZONE_SENTENCE =
  "The output is 9:16: keep the entire original ad content centered and unscaled in the middle 4:5 region of the frame, and fill the extra top and bottom areas ONLY with animated background that seamlessly extends the ad's scenery — no text, logos, products, or key subject elements may appear or move into those top/bottom bands.";

export function applyAdAnimatorSafeZone(prompt: string, aspectRatio: string): string {
  if (aspectRatio !== "9:16") return prompt;
  const base = prompt.trim();
  return base ? `${base} ${AD_ANIMATOR_SAFE_ZONE_SENTENCE}` : AD_ANIMATOR_SAFE_ZONE_SENTENCE;
}

// Maps a user-facing aspect ratio choice to a KIE Seedance-supported value.
// KIE supports: "1:1" | "4:3" | "3:4" | "16:9" | "9:16" | "21:9" | "adaptive".
// "Original" -> adaptive (keep the source image's ratio). "4:5" has no exact
// KIE equivalent; "3:4" is the closest portrait ratio.
export function adAnimatorAspectToKie(
  aspect: string,
): "1:1" | "4:3" | "3:4" | "16:9" | "9:16" | "21:9" | "adaptive" {
  switch (aspect) {
    case "9:16":
      return "9:16";
    case "4:5":
      return "3:4";
    case "1:1":
      return "1:1";
    case "16:9":
      return "16:9";
    case "Original":
    default:
      return "adaptive";
  }
}
